import type { ClientMessage, ServerMessage } from './collab-types';

// This URL will be updated after deploying the collab Worker
const COLLAB_WORKER_URL = 'https://pastewala-collab.admin-d10.workers.dev';

const CREATOR_PREFIX = 'pastewala-creator:';
const EDITCODE_PREFIX = 'pastewala-editcode:';

export function getCreatorToken(pasteId: string): string | null {
  const key = `${CREATOR_PREFIX}${pasteId}`;
  const stored = localStorage.getItem(key);
  if (stored) return stored;
  // Migrate tokens written by older builds into sessionStorage
  const legacy = sessionStorage.getItem(key);
  if (legacy) {
    localStorage.setItem(key, legacy);
    sessionStorage.removeItem(key);
    return legacy;
  }
  return null;
}

export function setCreatorToken(pasteId: string, token: string): void {
  localStorage.setItem(`${CREATOR_PREFIX}${pasteId}`, token);
}

export function getEditCode(pasteId: string): string | null {
  return localStorage.getItem(`${EDITCODE_PREFIX}${pasteId}`);
}

export function setEditCode(pasteId: string, code: string): void {
  localStorage.setItem(`${EDITCODE_PREFIX}${pasteId}`, code);
}

/** Forget a stored edit code — used when the server rejects it (wrong code). */
export function clearEditCode(pasteId: string): void {
  localStorage.removeItem(`${EDITCODE_PREFIX}${pasteId}`);
}

/** Paste ids this browser holds a creator token or edit code for. */
export function getOwnedPasteIds(): string[] {
  const ids = new Set<string>();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CREATOR_PREFIX)) ids.add(key.slice(CREATOR_PREFIX.length));
    if (key?.startsWith(EDITCODE_PREFIX)) ids.add(key.slice(EDITCODE_PREFIX.length));
  }
  return [...ids];
}

export async function createCollabSession(
  pasteId: string, content: string, creatorToken: string
): Promise<{ sessionId: string; collabUrl: string }> {
  const res = await fetch(`${COLLAB_WORKER_URL}/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pasteId, content, creatorToken }),
  });
  if (!res.ok) throw new Error('Failed to create collab session');
  return res.json();
}

export function connectToSession(
  sessionId: string,
  name: string,
  token: string,
  onMessage: (msg: ServerMessage) => void,
  onClose: () => void
): { send: (msg: ClientMessage) => void; close: () => void } {
  const wsUrl = `${COLLAB_WORKER_URL.replace('https://', 'wss://')}/session/${sessionId}/ws?name=${encodeURIComponent(name)}&token=${encodeURIComponent(token)}`;
  const ws = new WebSocket(wsUrl);

  ws.onmessage = (event) => {
    const msg: ServerMessage = JSON.parse(event.data);
    onMessage(msg);
  };
  ws.onclose = onClose;

  return {
    send: (msg: ClientMessage) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
    },
    close: () => ws.close(),
  };
}
